import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useOrganizations } from '../hooks/useOrganizations'
import { ApiError } from '../lib/errors'
import { TESTID } from '../lib/testids'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'

/** Derive Better Auth's URL-safe organization handle from its display name. */
export function slugifyOrganizationName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Toast for a refused creation: the Plotea abuse guards get their own copy. */
export function createErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return 'organizations.toast.createLimit'
    if (error.serverCode === 'email_not_verified')
      return 'organizations.toast.createNeedsVerifiedEmail'
  }
  return 'organizations.toast.error'
}

export function CreateOrganizationDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { create, setActive } = useOrganizations()
  const [name, setName] = useState('')
  const slug = slugifyOrganizationName(name)
  const busy = create.isPending || setActive.isPending

  const close = () => {
    setName('')
    onOpenChange(false)
  }

  const submit = () => {
    create.mutate(
      { name: name.trim(), slug },
      {
        onSuccess: (organization) => {
          setActive.mutate(organization.id, {
            onSuccess: () => {
              toast.success(t('organizations.toast.createdAndActivated'))
              close()
            },
            onError: () => {
              toast.warning(t('organizations.toast.createdActivationFailed'))
              close()
            },
          })
        },
        onError: (error) => toast.error(t(createErrorKey(error))),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !busy) close()
      }}
    >
      <DialogContent data-testid={TESTID.createOrgModal}>
        <DialogHeader>
          <DialogTitle>{t('organizations.create.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">
              {t('organizations.create.name')}
            </label>
            <Input
              autoFocus
              data-testid={TESTID.createOrgNameInput}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && slug && !busy) submit()
              }}
            />
            {slug && (
              <p className="text-xs text-muted-foreground">
                {t('organizations.create.slugPreview', { slug })}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            data-testid={TESTID.createOrgCancel}
            disabled={busy}
            onClick={close}
          >
            {t('organizations.create.cancel')}
          </Button>
          <Button
            data-testid={TESTID.createOrgSubmit}
            disabled={!slug || busy}
            onClick={submit}
          >
            {t('organizations.create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
